// src/components/desk/ClientLeadsFeed.jsx — unclaimed client DEMAND.
// ----------------------------------------------------------------------------
// Owner ruling 2026-08-06: campaign client leads land in the LEADS view under a
// `კლიენტი` type, properties stay under objects. Card built to the designer handoff
// "Client card design alignment" (2026-08-07) — see design/ for the mock.
//
// ⚠️ "LEAD" MEANS THE OPPOSITE THING HERE THAN IT DOES IN LeadsFeed.jsx. That file
// is owner SUPPLY (`property_lead`): a flat we could sell. This one is client DEMAND
// (`requirement`): a person who wants one. One pool, because an agent hunting new
// business wants both; nothing else shared — no table, no status vocabulary, no gate.
//
// ── THE FRAME IS THE PROPERTY CARD'S, AND THAT IS THE POINT ─────────────────
// Same `card` shell + `desk-cq`, same `desk-card-row`, same 224px state column via
// the exported `StateRow`, same `CooperationChips cardFooter hairline` rail. The
// alignment across the feed IS the feature: a client card and a property card must
// state their facts in the same places. No media column — a requirement has no photo,
// and an empty frame reads as broken data.
//
// It does NOT reuse `HistoryItem`: that component is built around a listing, and
// feeding it a requirement would mean faking one.
//
// ── 🚨 WHY THE CONTACT ONLY APPEARS AFTER PICK-UP ───────────────────────────
// The SERVER decides this, not the design. `POST /api/core/contact/reveal`
// (core_match.js) releases a civilian contact to an admin, a property representative,
// or an ACTIVE REPRESENTATIVE OF THAT REQUIREMENT. An unclaimed lead has none, so a
// call button on an unclaimed card would 403 every time. 0157 invariant 6 —
// permission follows the resource, never the person.
//
// So the rail's first slot is a CLAIM that becomes a CALL: pick up → you represent the
// requirement → the number is revealed → tap to dial. The card does not vanish on
// pick-up; an agent who just claimed a client wants to phone them now.
//
// ⚠️ Nothing here may fetch a number on hover, on scroll or on mount. The reveal is a
// recorded event (B111) and the feed payload carries no contact at all.

import { useEffect, useMemo, useRef, useState } from 'react'
// ⚠️ `tOr` for DYNAMIC keys — `t(key)` takes ONE argument and returns the KEY when
// missing, so `t(k, fallback)` silently ignores the fallback (every closed-deal badge
// read "desk_coop_end_deal_closed" until 2026-07-23). `tLog` for anything with a value
// inside a sentence: Georgian marks these with postpositions and case endings, so a
// translated word glued in front of a name is wrong in every one of them.
import { t, tOr, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { btn, card, pill, Modal, SkeletonCard } from './primitives.jsx'
import { ContactPill } from './ContactPill.jsx'
// cardAge/cardDateTime are the STATE COLUMN's formatter (დღეს · 3დ · 27დ · 3თვე),
// shared with the property card's განახლდა / ჩვენთან / შემოწმდა rows. Not relTime —
// that is the facts-column caption format, and mixing the two is the divergence the
// column exists to prevent.
import { StateRow, cardAge, cardDateTime } from './HistoryCards.jsx'
import { CooperationChips } from './CooperationChips.jsx'
import { getClientLeads, claimClientLead } from '../../db/core.js'

const fmtMoney = n => (n == null ? null : `$${Number(n).toLocaleString('en-US')}`)
const daysSince = iso => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)

// A range where either end may be absent. "from $400" and "up to $700" are both real
// answers; rendering "—" for the missing half shows the gap as if it were the value.
function range(min, max, fmt = v => v) {
  if (min == null && max == null) return null
  if (min != null && max != null) return min === max ? fmt(min) : `${fmt(min)}–${fmt(max)}`
  return min != null ? tLog('desk_cl_from', { v: fmt(min) }) : tLog('desk_cl_upto', { v: fmt(max) })
}

// ── Provenance: four cases, ORDERED FALLTHROUGH ─────────────────────────────
// ⚠️ The order is load-bearing, and the handoff's table alone is not enough. Measured
// 2026-08-07: all 13 rows currently in the pool carry `channel='scheduler'` AND no
// creator name — under "detected by channel" they would match the agent-typed case
// with nobody to name, and under "neither present" they would not match legacy either.
// Falling through campaign → client_form → creator-name → legacy resolves all 95
// requirements cleanly (1 ad / 39 link / 19 typed / 18 unrecorded).
//
// ⚠️ "sent the link" and "typed it" are DIFFERENT CLAIMS — one is the client's own
// words, the other an agent's retelling. Never collapse them into one "added by": the
// confidence tier below is built entirely on that distinction.
function provenanceOf(lead) {
  if (lead.campaign) {
    return { icon: 'megaphone', tier: 'self',
      text: tLog('desk_cl_prov_ad', { campaign: lead.campaign.name || lead.campaign.slug }) }
  }
  // A client we found in a Facebook group: the post text IS the client's own words (the
  // extension scraped their group post and committed the intake with channel='facebook_group'
  // — createClientRequirement in fb_group_sweep.js). Must sit BEFORE the created_by_agent_name
  // case: the agent who ran the scraper is the committing party, so that name is populated and
  // the "typed it" branch would otherwise win and wrongly claim an agent authored the demand.
  // Tier 'self' — the client wrote it, we only picked it up.
  if (lead.channel === 'facebook_group') {
    return { icon: 'globe', tier: 'self', text: t('desk_cl_prov_fbgroup') }
  }
  if (lead.channel === 'client_form') {
    return { icon: 'link', tier: 'self',
      text: lead.link_sent_by
        ? tLog('desk_cl_prov_link', { agent: lead.link_sent_by })
        : t('desk_cl_prov_link_anon') }
  }
  if (lead.created_by_agent_name) {
    return { icon: 'pencil', tier: 'agent',
      text: tLog('desk_cl_prov_typed', { agent: lead.created_by_agent_name }) }
  }
  return { icon: 'help-circle', tier: 'legacy', text: t('desk_cl_prov_legacy') }
}

// A state, never a score — no percentage, ever. It tells an agent how literally to read
// the criteria: a form the client filled says what they wanted; a row an agent typed has
// already been filtered through a person; a legacy row is simply unknown.
const TIER = {
  self:   { key: 'self',   dot: 'bg-emerald-500',        cls: 'text-emerald-600 dark:text-emerald-400' },
  agent:  { key: 'agent',  dot: 'bg-muted-foreground/50', cls: 'text-muted-foreground' },
  legacy: { key: 'legacy', dot: 'bg-amber-500',           cls: 'text-amber-600 dark:text-amber-400' },
}

export function ClientLeadCard({ lead, districtName, featName, onPickUp, busy, error, contact, matchCount,
                                 canReviewWithoutClaim }) {
  const [confirming, setConfirming] = useState(false)
  const c = lead.criteria || {}
  const prov = provenanceOf(lead)
  const tier = TIER[prov.tier]
  // Whichever column the client's surface wrote to — 0463's union. Reading only one is
  // the bug that made the district filter match everything.
  const codes = [...(c.district_must_codes || []), ...(c.district_codes || [])]
  const where = codes.length ? codes.map(code => districtName?.(code) || code).join(', ')
                             : t('desk_cl_whole_city')
  const budget = range(c.price_min_usd, c.price_max_usd, fmtMoney)
  const criteria = [
    range(c.rooms_min, c.rooms_max) && `${range(c.rooms_min, c.rooms_max)} ${t('dv_rooms')}`,
    range(c.area_sqm_min, c.area_sqm_max, v => `${v}m²`),
    c.occupants && `${c.occupants} ${t('desk_cl_people')}`,
    c.move_in_date && `${t('desk_cl_movein')} ${c.move_in_date}`,
  ].filter(Boolean)

  // Self-submitted answers "when did the client fill it in"; the rest answer "when did
  // this land with us". Different questions, so different labels — and the value is the
  // column's own formatter either way.
  const selfSubmitted = prov.tier === 'self'
  const freshTs = (selfSubmitted && lead.submitted_at) || lead.created_at
  const freshStale = freshTs ? daysSince(freshTs) > 7 : false

  // Rendered TWICE — beside the facts above the container breakpoint, stacked beneath
  // them below it. One definition, so the two layouts cannot drift into two different
  // answers about what the card states. Zero rows vanish; never "—", never 0.
  const stateRows = (<>
    <div className="flex items-center gap-1.5 pb-1">
      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${tier.dot}`} />
      <span className={`text-xs font-medium truncate ${tier.cls}`} title={t(`desk_cl_tier_${tier.key}_tip`)}>
        {t(`desk_cl_tier_${tier.key}`)}
      </span>
    </div>
    {/* A blank budget is a fact about the conversation, so the row still renders. */}
    <StateRow label={t('desk_cl_row_budget')}>
      {budget
        ? <span className="text-[13.5px] font-medium">{budget}</span>
        : <span className="text-muted-foreground">{t('desk_cl_no_budget_said')}</span>}
    </StateRow>
    {freshTs && (
      <StateRow label={t(selfSubmitted ? 'desk_cl_row_filled' : 'desk_cl_row_added')}
                title={cardDateTime(freshTs)}>
        <span className={freshStale ? 'text-amber-600 dark:text-amber-400 inline-flex items-center gap-1' : ''}>
          {freshStale && <Icon name="clock" size={11} />}
          {cardAge(freshTs)}
        </span>
      </StateRow>
    )}
    {contact?.claimedBy && (
      <StateRow label={t('desk_cl_row_rep')} wrap>{contact.claimedBy}</StateRow>
    )}
    {!contact?.claimedBy && lead.campaign && (
      <StateRow label={t('desk_cl_row_campaign')} wrap>{lead.campaign.name || lead.campaign.slug}</StateRow>
    )}
    {!contact?.claimedBy && !lead.campaign && lead.link_sent_by && (
      <StateRow label={t('desk_cl_row_link_sent')} wrap>{lead.link_sent_by}</StateRow>
    )}
    {!contact?.claimedBy && !lead.campaign && !lead.link_sent_by && lead.created_by_agent_name && (
      <StateRow label={t('desk_cl_row_typed')} wrap>{lead.created_by_agent_name}</StateRow>
    )}
  </>)

  const facts = (<>
    {/* Name is the anchor: this is a person, not inventory. Urgency rides this line
        inline, where the property card puts its ლიდი mark — not as a corner pill. */}
    <div className="flex items-center gap-1.5 min-w-0">
      <Icon name="user-check" size={16} className="shrink-0 text-muted-foreground" />
      <span className="text-[20px] font-semibold leading-7 tracking-[-0.01em] truncate">
        {lead.person_name || t('desk_cl_no_name')}
      </span>
      {lead.urgency === 'urgent' && (
        <span className="shrink-0 inline-flex items-center gap-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
          <Icon name="zap" size={12} />{t('desk_cl_urgent')}
        </span>
      )}
    </div>

    {/* The address slot — what the agent navigates by. */}
    <div className="mt-1 text-sm leading-5 text-foreground">
      {[tOr(`desk_cl_v_${lead.intent}`, lead.intent),
        lead.property_category && tOr(`desk_cl_v_${lead.property_category}`, lead.property_category),
        where].filter(Boolean).join(' · ')}
    </div>

    {criteria.length > 0
      ? <div className="mt-0.5 text-sm leading-5 text-muted-foreground">{criteria.join(' · ')}</div>
      : <div className="mt-0.5 text-sm leading-5 text-muted-foreground">{t('desk_cl_no_criteria')}</div>}

    {(c.household_codes?.length > 0 || c.has_pets) && (
      <div className="mt-1.5 flex flex-wrap gap-1">
        {(c.household_codes || []).map(code => (
          <span key={code} className={pill('neutral', { sm: true })}>{featName(code)}</span>
        ))}
        {c.has_pets && <span className={pill('neutral', { sm: true })}>{t('desk_cl_pets')}</span>}
      </div>
    )}

    <div className="mt-1.5 flex items-start gap-1.5 text-xs leading-[18px] text-muted-foreground">
      <Icon name={prov.icon} size={12} className="mt-0.5 shrink-0" />
      <span className="min-w-0">{prov.text}</span>
    </div>

    {lead.title && (
      <div className="mt-1 flex items-start gap-1.5 text-xs leading-[18px] text-muted-foreground">
        <Icon name="sticky-note" size={12} className="mt-0.5 shrink-0" />
        <span className="min-w-0 whitespace-pre-line line-clamp-3">{lead.title}</span>
      </div>
    )}
  </>)

  // ── Taking the client ─────────────────────────────────────────────────────
  // Dashed is already the system's word for "absence you can act on". The label keeps the
  // WORD because this is a claim, not a reveal — an icon-only phone would read as "call
  // now" and mislead.
  //
  // 📌 It sits SECOND, after the contact pill (owner, 2026-08-07). The number is the thing
  // an agent reaches for on every card in this feed, so it holds the same first-slot
  // position it holds on a property card; taking the client is the rarer, heavier act.
  //
  // 🚨 It asks first. Claiming is EXCLUSIVE (the route 409s a second agent) and writes a
  // representative that moves the client into the taker's own book — an act you cannot undo
  // from this card. It had no confirmation and no undo, and a mis-tap is exactly how two
  // real clients ended up held by someone who only meant to look (B380/B381). `user-check`
  // rather than `phone`: once the number lives on its own pill, a phone glyph here would be
  // the second phone button the 2026-07-25 ruling forbids.
  const claimControl = contact
    ? <span className={pill('ok')}><Icon name="user-check" size={12} />{t('desk_cl_you')}</span>
    : (<button type="button" className={pill('add', { door: true })} disabled={busy}
               title={t('desk_cl_pickup_title')} onClick={() => setConfirming(true)}>
        <Icon name="user-check" size={12} />{busy ? t('desk_cl_picking') : t('desk_cl_pickup')}
      </button>)

  // ── Review is not a claim (owner ruling 2026-08-07) ───────────────────────
  // An admin opening a lead to see what it is used to have exactly one door, and that
  // door WROTE a representative: it took the client out of the pool, put the admin's
  // name on the card as „წარმომადგენელი", and added them to nothing they could find
  // again (B380). Live cost before this: two real clients held and invisible.
  //
  // The server already permitted the honest version — `/api/core/contact/reveal` names
  // an admin as an allowed viewer — so this is a MISSING DOOR, not a new permission.
  // Nothing here decides access; the server still does, and still records the reveal.
  //
  // 🚨 It is a SECOND control, never a relabelling of the first. Management can also
  // legitimately take a client, so replacing pick-up would remove a real action. The
  // distinction the words must carry: one takes the client, one only looks.
  //
  // 📌 REBUILT 2026-08-07 (owner: "same pill as property, exactly"). The first version
  // hand-rolled a „ნომრის ნახვა" button, which broke the 2026-07-25 ruling in
  // CooperationChips — THE one contact affordance per card — the very rule written after
  // "two phone buttons on one card doing the same thing". It is now the real ContactPill
  // in party mode (0141), so the client's contact book behaves identically to a property's:
  // same geometry, same popover, same ★/✕ observation semantics, one logged reveal.
  // `requirementId` scopes the reveal door to THIS demand (0157 invariant 6) rather than
  // "knows the client somewhere".
  //
  // 🚨 SHOWN ONLY TO A VIEWER WHO CAN ACTUALLY REVEAL. ContactPill swallows a 403 into an
  // EMPTY popover, so rendering it for an agent who has not claimed would be a lock with
  // nothing behind it — the exact defect CLAUDE.md's diagnosis rule 6 names. An agent on an
  // unclaimed lead therefore sees only აღება, which IS the action they have.
  // Opening it does NOT claim: the reveal is logged, the card stays unclaimed, no
  // „წარმომადგენელი" row appears and აღება stays available. That pairing is the point —
  // the number is visible AND the lead is still there for an agent to take.
  const canSeeNumber = canReviewWithoutClaim || !!contact
  const contactControl = (canSeeNumber && lead.person_party_id) ? (
    <ContactPill chip iconOnly
      partyId={lead.person_party_id}
      partyName={lead.person_name || undefined}
      requirementId={lead.id}
      label={t('desk_coop_chip_default')} />
  ) : null

  return (
    <div className={`desk-cq ${card}`}>
      {/* The ask. Named consequences, not "are you sure": the two things a mis-tap actually
          does are that the client moves into YOUR book and leaves the shared pool, and both
          are stated. Cancel is the wide/default-looking action because the safe answer
          should be the easy one. */}
      {confirming && (
        <Modal title={t('desk_cl_confirm_title')} onClose={() => setConfirming(false)}>
          <div className="p-4">
            <p className="text-[13px] leading-5">
              {tLog('desk_cl_confirm_body', { name: lead.person_name || t('desk_cl_no_name') })}
            </p>
            <p className="mt-2 text-xs leading-[18px] text-muted-foreground">
              {t('desk_cl_confirm_note')}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className={btn('ghost')} onClick={() => setConfirming(false)}>
                {t('cancel')}
              </button>
              <button type="button" className={btn('primary')} disabled={busy}
                      onClick={() => { setConfirming(false); onPickUp(lead) }}>
                {t('desk_cl_confirm_ok')}
              </button>
            </div>
          </div>
        </Modal>
      )}
      <div className="desk-card-row">
        <div className="min-w-0 flex-1">
          {facts}
          {/* Narrow variant — same rows, stacked under the facts. */}
          <div className="desk-state-stacked mt-2 pt-1.5 border-t border-border/50">{stateRows}</div>
        </div>
        {/* Wide variant — the aligned 224px column. The alignment is the feature, so it
            must not flex with the facts beside it. */}
        <div className="desk-state-beside w-56 shrink-0 border-l border-border/50 pl-3 flex-col">{stateRows}</div>
      </div>

      {/* 409 is a real answer, not a failure — a muted line in place, never a red error. */}
      {error && <div className="mt-2 text-xs leading-[18px] text-muted-foreground">{error}</div>}

      {/* Contact FIRST — same slot the property card's 📞 occupies, so the thumb lands in
          the same place on every card in the feed regardless of type (§6's actual point).
          Taking the client follows it. */}
      <CooperationChips
        cardFooter hairline
        startSlot={contactControl
          ? <span className="inline-flex items-center gap-1.5">{contactControl}{claimControl}</span>
          : claimControl}
        endSlot={matchCount != null && matchCount > 0
          ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Icon name="house" size={11} />{tLog('desk_cl_matches', { n: matchCount })}
            </span>
          : null}
      />
    </div>
  )
}

export function ClientLeadsFeed({ intent, kind, districtCodes, priceMinUsd, priceMaxUsd,
                                 roomsMin, roomsMax, districts, features, agentId, ignoredFilters = [],
                                 onSnapshot, onPickedUp, canReviewWithoutClaim }) {
  // Names come from the LIVE `district` / `feature` tables passed as props, exactly as
  // ClientCriteriaSummary does — never an i18n namespace. There is no `feat_*` key space,
  // and inventing one renders every chip as its raw code.
  const districtName = code => { const d = districts?.find(x => x.code === code); return d ? (d.name_ka || d.name_en || code) : code }
  const featName = code => { const f = features?.find(x => x.code === code); return f ? (f.name_ka || f.name_en || code) : code }

  const [state, setState] = useState({ loading: true, leads: [], count: null, capped: false, error: null })
  const [busyId, setBusyId] = useState(null)
  const [errById, setErrById] = useState({})
  const [contactById, setContactById] = useState({})

  // The filter identity, so the effect refires on a real change rather than on a new
  // array literal every render (the `since`-in-deps lesson from LeadsFeed).
  const sig = useMemo(() => JSON.stringify([intent, kind, districtCodes, priceMinUsd, priceMaxUsd, roomsMin, roomsMax]),
    [intent, kind, districtCodes, priceMinUsd, priceMaxUsd, roomsMin, roomsMax])

  // review 2026-09-23: PAGED. The list fetched 100 once while the header counted to 300, so the
  // header promised rows the list could never reach. The route takes `offset`, so further pages
  // load on demand. `sigRef` drops a page that returns after the filters changed; `claimedRef`
  // counts pick-ups made here, because a claimed lead leaves the server's pool while staying on
  // screen, and the next offset must not skip the lead that slid into its place.
  const PAGE = 100
  const sigRef = useRef(sig)
  const claimedRef = useRef(0)
  const [more, setMore] = useState({ busy: false, done: false, error: null })
  useEffect(() => {
    let dead = false
    sigRef.current = sig
    claimedRef.current = 0
    setMore({ busy: false, done: false, error: null })
    setState(s => ({ ...s, loading: true, error: null }))
    getClientLeads({ intent, kind, districtCodes, priceMinUsd, priceMaxUsd, roomsMin, roomsMax, limit: PAGE })
      .then(r => {
        if (dead) return
        setState({ loading: false, leads: r.leads, count: r.count, capped: r.countCapped, error: null })
        if (r.leads.length < PAGE) setMore(m => ({ ...m, done: true }))
        onSnapshot?.(r.count, r.countCapped)
      })
      .catch(e => { if (!dead) setState({ loading: false, leads: [], count: null, capped: false, error: e.message }) })
    return () => { dead = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig])

  async function loadMore() {
    if (more.busy || more.done) return
    const mySig = sig
    setMore(m => ({ ...m, busy: true, error: null }))
    try {
      const r = await getClientLeads({ intent, kind, districtCodes, priceMinUsd, priceMaxUsd, roomsMin, roomsMax,
        limit: PAGE, offset: Math.max(0, state.leads.length - claimedRef.current), withCount: false })
      if (sigRef.current !== mySig) return
      setState(s => {
        const seen = new Set(s.leads.map(l => l.id))
        return { ...s, leads: [...s.leads, ...r.leads.filter(l => !seen.has(l.id))] }
      })
      setMore({ busy: false, done: r.leads.length < PAGE, error: null })
    } catch (e) {
      if (sigRef.current === mySig) setMore({ busy: false, done: false, error: e.message })
    }
  }

  async function pickUp(lead) {
    setBusyId(lead.id)
    setErrById(m => ({ ...m, [lead.id]: null }))
    try {
      await claimClientLead(lead.id, agentId)
      // No reveal here (review 2026-09-23): this used to fetch the contact book and store a
      // phone the card never rendered — the number reaches the agent only through ContactPill,
      // which logs its OWN reveal — so every pick-up wrote a reveal record for nothing shown.
      setContactById(m => ({ ...m, [lead.id]: { claimedBy: t('desk_cl_you') } }))
      claimedRef.current++   // review 2026-09-23: it left the server's pool — see loadMore's offset
      setState(s => ({ ...s, count: s.count == null ? null : Math.max(0, s.count - 1) }))
      onPickedUp?.(lead)
    } catch (e) {
      // A 409 carrying `mine` is the viewer's OWN earlier claim (another tab, a double tap):
      // that is success, not "taken by someone else".
      if (e?.status === 409 && e?.body?.mine) {
        setContactById(m => ({ ...m, [lead.id]: { claimedBy: t('desk_cl_you') } }))
        onPickedUp?.(lead)
        return
      }
      const msg = /409|already/i.test(e.message) ? t('desk_cl_taken') : e.message
      setErrById(m => ({ ...m, [lead.id]: msg }))
    } finally { setBusyId(null) }
  }

  // No reveal handler here any more (2026-08-07): ContactPill owns the reveal in party
  // mode, so routing it through this component would have been a second implementation of
  // one logged act.
  if (state.loading) return <div className="space-y-2">{[0, 1, 2].map(i => <SkeletonCard key={i} />)}</div>
  if (state.error) return <div className="p-4 text-[13px] text-rose-600">{state.error}</div>
  if (!state.leads.length) {
    return <div className="p-6 text-center text-[13px] leading-5 text-muted-foreground">{t('desk_cl_empty')}</div>
  }

  return (
    <div className="space-y-2">
      <div className="px-0.5 text-[11px] leading-4 text-muted-foreground">
        {/* "N+" when the count saturated at the RPC's 300 ceiling. A capped number drawn
            as a total is the `92 → 162 → 3141` failure in slow motion. */}
        {state.count != null && `${state.count}${state.capped ? '+' : ''} ${t('desk_type_client_leads')}`}
      </div>
      {/* DF04 (2026-10-28): what the panel shows but this feed cannot ask — said, not silently widened. */}
      {ignoredFilters.length > 0 && (
        <div className="flex items-start gap-1 px-0.5 text-[11px] leading-4 text-amber-600 dark:text-amber-400">
          <Icon name="alert-triangle" size={12} className="mt-0.5 shrink-0" />
          <span>{tLog('desk_cl_ignored', { list: ignoredFilters.join(', ') })}</span>
        </div>
      )}
      {state.leads.map(l => (
        <ClientLeadCard key={l.id} lead={l} districtName={districtName} featName={featName}
          onPickUp={pickUp} busy={busyId === l.id} error={errById[l.id]} contact={contactById[l.id]}
          canReviewWithoutClaim={canReviewWithoutClaim} />
      ))}
      {/* review 2026-09-23: the rest of what the header counts. Shown while the last page came
          back full; a short page means the pool is exhausted. */}
      {!more.done && (
        <div className="flex flex-col items-center gap-1 py-2">
          <button type="button" className={btn('ghost')} disabled={more.busy} onClick={loadMore}>
            {/* Language-neutral label: i18n.js is outside this change, and no generic
                "load more" key exists. Shows loaded / counted, same numbers as the header. */}
            {more.busy ? t('desk_pagination_loading')
              : <span className="inline-flex items-center gap-1"><Icon name="chevron-down" size={12} />
                  {state.leads.length}{state.count != null ? ` / ${state.count}${state.capped ? '+' : ''}` : ''}</span>}
          </button>
          {more.error && <div className="text-xs text-muted-foreground">{more.error}</div>}
        </div>
      )}
    </div>
  )
}
